import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import type { ActionModuleInstanceModel } from '@/api/model'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcn/table'
import { Button } from '@/components/shadcn/button'
import { LoaderTable } from '@/components/common/loaders/LoaderTable'
import { cn } from '@/lib/utils'
import { fmtDate } from '@/pages/incentivation/lib'
import type { IncentivationFilterValues } from './IncentivationFilters'
import { ScoreCell } from './ScoreCell'
import { useInstances } from './useInstances'

interface Props {
  moduleId: number
  employeeId: string
  applied: IncentivationFilterValues
  selectedInstanceId: number | null
  onSelectInstance: (instance: ActionModuleInstanceModel) => void
  /** When the details column is open, keep period + score and drop the rest to save width. */
  compact: boolean
  /** X in the header steps back to the employee list (clears the employee selection). */
  onClose: () => void
}

/**
 * Column 2 of the drill-down: the selected employee's instances over the filtered range
 * (period, score, rewarded points). Selecting a row opens the details column. The close X
 * lives in the trailing header cell.
 */
export function InstanceColumn({ moduleId, employeeId, applied, selectedInstanceId, onSelectInstance, compact, onClose }: Props) {
  const { t } = useTranslation('action-modules-detail')
  const { instances, isLoading } = useInstances(moduleId, employeeId, applied)

  // period + score (+ reward status + points when not compact) + the trailing close column
  const colCount = (compact ? 2 : 4) + 1

  const closeButton = (
    <Button
      variant="ghost"
      size="icon"
      className="-mr-1 size-6"
      aria-label={t('incentivation.close')}
      title={t('incentivation.close')}
      onClick={onClose}
    >
      <X className="size-4" />
    </Button>
  )

  return (
    <div className="flex min-h-full flex-col">
      {/* No own scroll area: the panel row on the tab scrolls all columns together. */}
      <div className="flex-1">
        <Table>
          <TableHeader className="bg-card shadow-sm [&_th]:bg-card">
            <TableRow>
              <TableHead>{t('incentivation.columns.period')}</TableHead>
              <TableHead>{t('incentivation.columns.score')}</TableHead>
              {!compact && <TableHead>{t('incentivation.columns.rewardStatus')}</TableHead>}
              {!compact && <TableHead className="text-right">{t('incentivation.columns.rewardedPoints')}</TableHead>}
              <TableHead className="w-9 text-right">{closeButton}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <LoaderTable
              isLoading={isLoading}
              isEmpty={instances.length === 0}
              colSpan={colCount}
              emptyMessage={t('incentivation.noInstances')}
            >
              {instances.map((inst) => {
                const isSelected = inst.id === selectedInstanceId
                return (
                  <TableRow
                    key={inst.id}
                    className={cn('cursor-pointer', isSelected && 'bg-muted/40')}
                    onClick={() => onSelectInstance(inst)}
                  >
                    <TableCell className="whitespace-nowrap">
                      {inst.startDate || inst.endDate ? `${fmtDate(inst.startDate)} – ${fmtDate(inst.endDate)}` : '—'}
                    </TableCell>
                    <TableCell><ScoreCell data={inst.incentivationData} /></TableCell>
                    {!compact && (
                      <TableCell>
                        {inst.rewardStatus
                          ? t(`incentivation.rewardStatus.${inst.rewardStatus}`, { defaultValue: inst.rewardStatus })
                          : '—'}
                      </TableCell>
                    )}
                    {!compact && (
                      <TableCell className="text-right font-medium">{inst.incentivationData?.reward ?? '—'}</TableCell>
                    )}
                    <TableCell />
                  </TableRow>
                )
              })}
            </LoaderTable>
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
